import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Link,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../services/api";
import { driveBase } from "../config";

const maxDataAceitacao = (() => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
})();

const MAX_ARQUIVO_SGT_BYTES = 10 * 1024 * 1024;

const validarTamanhoArquivoSgt = (file) => {
  if (file.size <= MAX_ARQUIVO_SGT_BYTES) return true;
  alert("Arquivos integrados ao SGT no PotencializEE Nacional devem ter no máximo 10 MB.");
  return false;
};

const statusLabel = {
  pendente: { label: "Aguardando validação DR", color: "warning" },
  aprovado: { label: "Documentação conforme", color: "success" },
  reprovado: { label: "Documentação reprovada", color: "error" },
};

/** Bloco de proposta comercial + validação de documentação pelo colaborador DR (M&A/PROCEL). */
const PropostaComercialSection = ({
  processoId,
  podeEditarProposta,
  podeValidarDR,
  cartaoCnpj,
  onCartaoCnpjAtualizado,
  autodeclaracao,
  onAutodeclaracaoAtualizada,
}) => {
  const [proposta, setProposta] = React.useState(null);
  const [dataAceitacao, setDataAceitacao] = React.useState("");
  const [motivoReprovacao, setMotivoReprovacao] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [cartaoCnpjFile, setCartaoCnpjFile] = React.useState(cartaoCnpj || null);
  const [uploadingCartaoCnpj, setUploadingCartaoCnpj] = React.useState(false);
  const [autodeclaracaoFile, setAutodeclaracaoFile] = React.useState(autodeclaracao || null);
  const [uploadingAutodeclaracao, setUploadingAutodeclaracao] = React.useState(false);

  React.useEffect(() => {
    setCartaoCnpjFile(cartaoCnpj || null);
  }, [cartaoCnpj]);

  React.useEffect(() => {
    setAutodeclaracaoFile(autodeclaracao || null);
  }, [autodeclaracao]);

  const carregar = React.useCallback(async () => {
    if (!processoId) return;
    const { data } = await api.http.get(`/propostas-comerciais/${processoId}`);
    setProposta(data);
    setDataAceitacao(data?.dataAceitacaoProposta || "");
  }, [processoId]);

  React.useEffect(() => {
    carregar();
  }, [carregar]);

  const handleUpload = async (event) => {
    if (event.target.files.length === 0) return;
    if (!validarTamanhoArquivoSgt(event.target.files[0])) return;
    if (dataAceitacao && dataAceitacao > maxDataAceitacao) {
      alert("A data de assinatura/aceitação não pode passar de 1 mês da data de inserção da proposta.");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", event.target.files[0]);
      formData.append("processoId", processoId);
      const { data: arquivo } = await api.http.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.http.post("/propostas-comerciais", {
        processoId,
        arquivoId: arquivo.id,
        dataAceitacaoProposta: dataAceitacao || undefined,
      });
      setProposta(data);
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarData = async () => {
    if (dataAceitacao && dataAceitacao > maxDataAceitacao) {
      alert("A data de assinatura/aceitação não pode passar de 1 mês da data de inserção da proposta.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.http.post("/propostas-comerciais", {
        processoId,
        dataAceitacaoProposta: dataAceitacao || undefined,
      });
      setProposta(data);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCartaoCnpj = async (event) => {
    if (event.target.files.length === 0) return;
    if (!validarTamanhoArquivoSgt(event.target.files[0])) return;
    setUploadingCartaoCnpj(true);
    try {
      const formData = new FormData();
      formData.append("file", event.target.files[0]);
      formData.append("field", "cartaoCnpjId");
      const { data: processoAtualizado } = await api.http.post(
        `/processos/${processoId}/upload`,
        formData
      );
      setCartaoCnpjFile(processoAtualizado.cartaoCnpj);
      if (onCartaoCnpjAtualizado) onCartaoCnpjAtualizado(processoAtualizado);
    } finally {
      setUploadingCartaoCnpj(false);
    }
  };

  const handleUploadAutodeclaracao = async (event) => {
    if (event.target.files.length === 0) return;
    if (!validarTamanhoArquivoSgt(event.target.files[0])) return;
    setUploadingAutodeclaracao(true);
    try {
      const formData = new FormData();
      formData.append("file", event.target.files[0]);
      formData.append("field", "certNegDebtTrabId");
      const { data: processoAtualizado } = await api.http.post(
        `/processos/${processoId}/upload`,
        formData
      );
      setAutodeclaracaoFile(processoAtualizado.certNegDebtTrab);
      if (onAutodeclaracaoAtualizada) onAutodeclaracaoAtualizada(processoAtualizado);
    } finally {
      setUploadingAutodeclaracao(false);
    }
  };

  const handleValidar = async (statusConformidade) => {
    if (statusConformidade === "reprovado" && !motivoReprovacao) {
      alert("Informe o motivo da reprovação da documentação.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.http.put(`/propostas-comerciais/${proposta.id}`, {
        statusConformidade,
        motivoReprovacao: statusConformidade === "reprovado" ? motivoReprovacao : undefined,
      });
      setProposta(data);
      setMotivoReprovacao("");
    } finally {
      setLoading(false);
    }
  };

  const status = statusLabel[proposta?.statusConformidade || "pendente"];

  return (
    <>
      <Divider>
        <h3>Proposta comercial</h3>
      </Divider>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", my: 2 }}>
          {proposta && <Chip label={status.label} color={status.color} />}

          {proposta?.arquivo && (
            <Alert severity="info" sx={{ width: "100%" }}>
              Proposta enviada:{" "}
              <Link
                href={driveBase + proposta.arquivo.fileRef}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {proposta.arquivo.fileName}
                <DownloadIcon fontSize="small" />
              </Link>
            </Alert>
          )}

          {podeEditarProposta && (
            <>
              <label htmlFor="proposta-comercial-upload">
                <input
                  id="proposta-comercial-upload"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleUpload}
                />
                <Button variant="outlined" component="span" endIcon={<CloudUploadIcon />}>
                  {proposta?.arquivo ? "Substituir proposta comercial (pdf)" : "Enviar proposta comercial (pdf)"}
                </Button>
              </label>

              <FormControl sx={{ width: "100%" }}>
                <TextField
                  type="date"
                  label="Data de assinatura/aceitação da proposta"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: maxDataAceitacao }}
                  helperText="Não pode ser mais de 1 mês após a data de inserção da proposta"
                  value={dataAceitacao}
                  onChange={(event) => setDataAceitacao(event.target.value)}
                  onBlur={handleSalvarData}
                />
              </FormControl>
            </>
          )}

          {cartaoCnpjFile && (
            <Alert severity="info" sx={{ width: "100%" }}>
              Cartão CNPJ enviado:{" "}
              <Link
                href={driveBase + cartaoCnpjFile.fileRef}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {cartaoCnpjFile.fileName}
                <DownloadIcon fontSize="small" />
              </Link>
            </Alert>
          )}

          {podeEditarProposta && (
            <label htmlFor="cartao-cnpj-upload">
              <input
                id="cartao-cnpj-upload"
                type="file"
                style={{ display: "none" }}
                onChange={handleUploadCartaoCnpj}
              />
              <Button
                variant="outlined"
                component="span"
                endIcon={<CloudUploadIcon />}
                disabled={uploadingCartaoCnpj}
              >
                {uploadingCartaoCnpj
                  ? "Enviando..."
                  : cartaoCnpjFile
                  ? "Substituir cartão CNPJ"
                  : "Enviar cartão CNPJ"}
              </Button>
            </label>
          )}

          {autodeclaracaoFile && (
            <Alert severity="info" sx={{ width: "100%" }}>
              Autodeclaração de elegibilidade assinada enviada:{" "}
              <Link
                href={driveBase + autodeclaracaoFile.fileRef}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {autodeclaracaoFile.fileName}
                <DownloadIcon fontSize="small" />
              </Link>
            </Alert>
          )}

          {podeEditarProposta && (
            <label htmlFor="autodeclaracao-upload">
              <input
                id="autodeclaracao-upload"
                type="file"
                style={{ display: "none" }}
                onChange={handleUploadAutodeclaracao}
              />
              <Button
                variant="outlined"
                component="span"
                endIcon={<CloudUploadIcon />}
                disabled={uploadingAutodeclaracao}
              >
                {uploadingAutodeclaracao
                  ? "Enviando..."
                  : autodeclaracaoFile
                  ? "Substituir autodeclaração de elegibilidade assinada"
                  : "Enviar autodeclaração de elegibilidade assinada"}
              </Button>
            </label>
          )}

          {proposta?.statusConformidade === "reprovado" && proposta.motivoReprovacao && (
            <Alert severity="error" sx={{ width: "100%" }}>
              Motivo da reprovação: {proposta.motivoReprovacao}
            </Alert>
          )}

          {podeValidarDR && proposta && proposta.statusConformidade !== "aprovado" && (
            <>
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  label="Motivo da reprovação (obrigatório para reprovar)"
                  multiline
                  rows={3}
                  value={motivoReprovacao}
                  onChange={(event) => setMotivoReprovacao(event.target.value)}
                />
              </FormControl>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button variant="contained" color="success" onClick={() => handleValidar("aprovado")}>
                  Aprovar documentação
                </Button>
                <Button variant="outlined" color="error" onClick={() => handleValidar("reprovado")}>
                  Reprovar documentação
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default PropostaComercialSection;
